import { SegmentConstraint } from '../types/api';
import { Point } from '../services/edgeApi';

type Serializable = string | number | boolean | null | undefined;

/**
 * Builds a query string from an object
 * @returns a string containing a query string suitable for use in a URL. Does not include the question mark.
 * @throws {Error} if params is not `object | null | undefined` or if any value is an object
 */
export const buildQueryString = (
  params: Record<string, Serializable | Serializable[]> | null = {},
) => {
  if (params === null || params === undefined) return '';

  if (Array.isArray(params))
    throw new Error('params must be an object, not an array');
  if (typeof params !== 'object')
    throw new Error(`params must be an object, not a ${typeof params}`);

  const url = new URL('http://localhost');
  Object.keys(params).forEach((key) => {
    if (params[key] === null || params[key] === undefined || params[key] === '')
      return;

    if (Array.isArray(params[key])) {
      params[key].forEach((value) => {
        if (value === null || value === undefined) return;
        url.searchParams.append(key, value.toString());
      });
      return;
    }

    if (typeof params[key] === 'object') {
      throw new Error(
        `value cannot be an object, key: ${key}, value: ${JSON.stringify(
          params[key],
        )}`,
      );
    }

    url.searchParams.append(key, params[key].toString());
  });
  return url.searchParams.toString();
};

import { toIsoDate } from './dateUtils';
export { toIsoDate };

/**
 * @brief Replaces multiple linebreaks with a single linebreak
 * @returns str with all line breaks replaced with a single line break
 */
export const trimLineBreaks = (str: string) => {
  if (str === null || str === undefined) return '';
  if (typeof str !== 'string')
    throw new TypeError(`str must be a string, not a ${typeof str}`);

  return str.replace(/(\n|\r){2,}/gm, '\n');
};

/**
 * @param str - The string to be titleized
 * @description Capitalizes the first letter of each word in a string
 * This function handles both spaces and hyphens as word separators.
 * It also converts the entire string to lowercase before capitalizing.
 * @throws {TypeError} if str is not a string
 * @returns str with the first letter of each word capitalized
 */
export const titleize = (str: string) => {
  if (str === null || str === undefined) return '';
  if (typeof str !== 'string')
    throw new TypeError(`str must be a string, not a ${typeof str}`);

  return str
    .toLowerCase()
    .split(' ')
    .map((word) => {
      if (word.length < 1) return word;
      return word[0].toUpperCase() + word.slice(1, undefined);
    })
    .join(' ')
    .split('-')
    .map((word) => {
      if (word.length < 1) return word;
      return word[0].toUpperCase() + word.slice(1, undefined);
    })
    .join('-');
};

/**
 *
 * @returns html link element with the given text and url
 */
export const toLink = (text: string, url: string) => {
  if (text === null || text === undefined) return '';
  if (typeof text !== 'string')
    throw new TypeError(`text must be a string, not a ${typeof text}`);
  if (url === null || url === undefined || URL.canParse(url) === false)
    return '';
  if (typeof url !== 'string')
    throw new TypeError(`url must be a string, not a ${typeof url}`);

  const link = document.createElement('a');
  link.href = url;
  link.innerText = text;
  link.textContent = text;
  return link.outerHTML;
};

export const hashString = (str: string) => {
  var hash = 0;
  if (str.length === 0) return hash;
  for (let i = 0; i < str.length; i++) {
    const chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

export const constraintToString = (
  source: Point,
  target: Point,
  constraint: SegmentConstraint,
) => {
  const sourceName = source?.name || source?.id.toString();
  const targetName = target?.name || target?.id.toString();
  if (constraint.flow_direction === 'FORWARDHAUL') {
    return `From ${sourceName} to ${targetName}`;
  }
  return `From ${targetName} to ${sourceName}`;
};

export const toZoneName = (zone: string) => {
  if (zone.match(/^[0-9]+$/i)) {
    return `Zone ${zone}`;
  }
  return zone;
};
